/**
 * EmployeePickerModal 컴포넌트
 *
 * **역할:**
 * - 직원 목록을 표시하고 한 명을 선택하는 모달
 * - 조직장/승인자 지정 시 사용
 *
 * **사용 예시:**
 * ```tsx
 * const [isPickerOpen, setIsPickerOpen] = useState(false);
 * const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
 *
 * <EmployeePickerModal
 *   isOpen={isPickerOpen}
 *   onClose={() => setIsPickerOpen(false)}
 *   onSelect={(employeeId, employeeName) => {
 *     setSelectedEmployeeId(employeeId);
 *     setIsPickerOpen(false);
 *   }}
 *   title="조직장 선택"
 * />
 * ```
 */

import React, { useState } from 'react';
import { X, Search, User } from 'lucide-react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';
import { useEmployees } from '../../../shared/hooks/useEmployees';
import { Employee } from '../../../shared/types/employee';

/**
 * EmployeePickerModal Props
 */
export interface EmployeePickerModalProps {
  /**
   * 모달 열림 여부
   */
  isOpen: boolean;

  /**
   * 모달 닫기 콜백
   */
  onClose: () => void;

  /**
   * 직원 선택 콜백
   *
   * @param employeeId 선택된 직원 ID
   * @param employeeName 선택된 직원 이름
   */
  onSelect: (employeeId: string, employeeName: string) => void;

  /**
   * 모달 제목 (선택 사항)
   */
  title?: string;

  /**
   * 현재 선택된 직원 ID (선택 사항)
   */
  selectedEmployeeId?: string | null;
}

/**
 * EmployeePickerModal 컴포넌트
 */
export function EmployeePickerModal({
  isOpen,
  onClose,
  onSelect,
  title = '직원 선택',
  selectedEmployeeId,
}: EmployeePickerModalProps) {
  // ============================================
  // 상태 관리
  // ============================================

  const [searchText, setSearchText] = useState<string>('');

  // 직원 목록 조회 (검색 필터 적용)
  const { employees, isLoading, error } = useEmployees({
    searchText,
    status: 'active', // 재직자만
  });

  // ============================================
  // 이벤트 핸들러
  // ============================================

  /**
   * 직원 선택 핸들러
   */
  const handleSelect = (employee: Employee) => {
    onSelect(employee.id, employee.nameKo);
  };

  /**
   * 선택 해제 핸들러
   */
  const handleClear = () => {
    onSelect('', ''); // 빈 값으로 전달하여 선택 해제
  };

  // ============================================
  // 모달이 닫혀있으면 렌더링하지 않음
  // ============================================

  if (!isOpen) {
    return null;
  }

  // ============================================
  // 렌더링
  // ============================================

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1100, // OrgUnitFormModal보다 위에 표시
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()} // 내부 클릭 시 닫기 방지
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={24} style={{ color: 'var(--color-gray-600)' }} />
          </button>
        </div>

        {/* 검색 바 */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--color-gray-200)' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-gray-400)',
              }}
            />
            <input
              type="text"
              placeholder="이름, 이메일, 사번으로 검색..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
              }}
              autoFocus
            />
          </div>
        </div>

        {/* 선택 해제 버튼 */}
        {selectedEmployeeId && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--color-gray-200)' }}>
            <button
              onClick={handleClear}
              style={{
                padding: '8px 16px',
                background: 'var(--color-gray-100)',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              선택 해제
            </button>
          </div>
        )}

        {/* 직원 목록 */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 0' }}>
          {isLoading && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
              직원 목록을 불러오는 중...
            </div>
          )}

          {error && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-error)' }}>
              직원 목록 조회 실패: {error.message}
            </div>
          )}

          {!isLoading && !error && employees.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
              <User size={48} style={{ margin: '0 auto 16px' }} />
              <p>검색 결과가 없습니다.</p>
            </div>
          )}

          {!isLoading && !error && employees.length > 0 && (
            <div>
              {employees.map((employee) => (
                <button
                  key={employee.id}
                  onClick={() => handleSelect(employee)}
                  style={{
                    width: '100%',
                    padding: '12px 24px',
                    border: 'none',
                    background:
                      selectedEmployeeId === employee.id
                        ? 'var(--color-primary-light, #E0F2FE)'
                        : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s ease',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedEmployeeId !== employee.id) {
                      e.currentTarget.style.background = 'var(--color-gray-50)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedEmployeeId !== employee.id) {
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-gray-800)' }}>
                      {employee.nameKo}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                      {employee.email || '-'} | {employee.employeeNumber || '-'} |{' '}
                      {employee.orgUnitName || '-'}
                    </div>
                  </div>
                  {selectedEmployeeId === employee.id && (
                    <span
                      style={{
                        padding: '4px 8px',
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      선택됨
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'var(--color-gray-200)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
